import { createContext, useContext, useEffect, useState } from 'react'
import { useParams, useNavigate, Outlet } from 'react-router-dom'
import {
  Edit2,
  Trash2,
  Save,
  X,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  History,
  Ban,
} from 'lucide-react'
import { toast } from 'sonner'
import { useAuth } from '../../contexts/AuthContext'
import { requestService, UpdateRequestData } from '../../services/requestService'
import { LicenseRequest, REQUEST_TYPE_LABELS, REQUEST_STATUS_LABELS } from '../../types'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { DetailLayout } from '../../components/ui/DetailLayout'
import { TabItem } from '../../components/ui/DetailTabs'

interface FormDataType {
  startDate: string
  endDate: string
  secondDate: string
  notes: string
  attachmentUrl: string
  attachmentName: string
}

interface RequestContextType {
  request: LicenseRequest | null
  otherRequests: LicenseRequest[]
  isLoading: boolean
  isAdmin: boolean
  isOwner: boolean
  isEditing: boolean
  isSaving: boolean
  formData: FormDataType
  setIsEditing: (value: boolean) => void
  setFormData: (data: FormDataType) => void
  handleSave: () => Promise<void>
  fetchRequest: () => Promise<void>
  canEdit: boolean
}

const RequestContext = createContext<RequestContextType | null>(null)

export function useRequestContext() {
  const context = useContext(RequestContext)
  if (!context) {
    throw new Error('useRequestContext must be used within RequestDetailLayout')
  }
  return context
}

const StatusIcon = ({ status }: { status: string }) => {
  switch (status) {
    case 'APPROVED':
      return <CheckCircle className="h-5 w-5" />
    case 'DENIED':
      return <XCircle className="h-5 w-5" />
    default:
      return <Clock className="h-5 w-5" />
  }
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'APPROVED':
      return 'text-success-600 bg-success-100 dark:bg-success-900/30'
    case 'DENIED':
      return 'text-error-600 bg-error-100 dark:bg-error-900/30'
    default:
      return 'text-warning-600 bg-warning-100 dark:bg-warning-900/30'
  }
}

export function RequestDetailLayout() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const isAdmin = user?.role === 'ADMIN'

  const [request, setRequest] = useState<LicenseRequest | null>(null)
  const [otherRequests, setOtherRequests] = useState<LicenseRequest[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const [showApproveConfirm, setShowApproveConfirm] = useState(false)
  const [showDenyConfirm, setShowDenyConfirm] = useState(false)
  const [cancellationReason, setCancellationReason] = useState('')
  const [denialReason, setDenialReason] = useState('')
  const [approvalNotes, setApprovalNotes] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)

  const [formData, setFormData] = useState<FormDataType>({
    startDate: '',
    endDate: '',
    secondDate: '',
    notes: '',
    attachmentUrl: '',
    attachmentName: '',
  })

  useEffect(() => {
    if (id) {
      fetchRequest()
    }
  }, [id])

  useEffect(() => {
    if (request?.employeeDbId) {
      fetchOtherRequests()
    }
  }, [request?.employeeDbId])

  const fetchRequest = async () => {
    try {
      const data = await requestService.getById(id!)
      setRequest(data)
      setFormData({
        startDate: data.startDate.split('T')[0],
        endDate: data.endDate.split('T')[0],
        secondDate: data.secondDate ? data.secondDate.split('T')[0] : '',
        notes: data.notes || '',
        attachmentUrl: data.attachmentUrl || '',
        attachmentName: data.attachmentName || '',
      })
    } catch (error) {
      toast.error('Error al cargar solicitud')
      navigate('/requests')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchOtherRequests = async () => {
    if (!request?.employeeDbId) return
    try {
      const data = await requestService.getByEmployee(request.employeeDbId)
      setOtherRequests(data.filter(r => r.id !== id))
    } catch (error) {
      console.error('Error fetching other requests:', error)
    }
  }

  const handleSave = async () => {
    if (!request) return
    setIsSaving(true)
    try {
      const updateData: UpdateRequestData = {
        startDate: formData.startDate,
        endDate: formData.endDate,
        secondDate: formData.secondDate || undefined,
        notes: formData.notes || undefined,
        attachmentUrl: formData.attachmentUrl || undefined,
        attachmentName: formData.attachmentName || undefined,
      }
      await requestService.update(request.id, updateData)
      toast.success('Solicitud actualizada')
      setIsEditing(false)
      fetchRequest()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al guardar')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!request) return
    try {
      await requestService.delete(request.id)
      toast.success('Solicitud eliminada')
      navigate('/requests')
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al eliminar')
    }
  }

  const handleCancel = async () => {
    if (!request) return
    try {
      await requestService.cancel(request.id, cancellationReason || undefined)
      toast.success('Solicitud cancelada', {
        description: isAdmin
          ? 'El empleado ha sido notificado.'
          : 'Se ha notificado al responsable de RRHH.',
      })
      setShowCancelConfirm(false)
      setCancellationReason('')
      fetchRequest()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al cancelar')
    }
  }

  const handleApprove = async () => {
    if (!request) return
    setIsProcessing(true)
    try {
      await requestService.approve(request.id, approvalNotes || undefined)
      toast.success('Solicitud aprobada', {
        description: 'El empleado ha sido notificado.',
      })
      setShowApproveConfirm(false)
      setApprovalNotes('')
      fetchRequest()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al aprobar')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleDeny = async () => {
    if (!request || !denialReason.trim()) {
      toast.error('Debes indicar el motivo de denegación')
      return
    }
    setIsProcessing(true)
    try {
      await requestService.deny(request.id, denialReason)
      toast.success('Solicitud denegada', {
        description: 'El empleado ha sido notificado.',
      })
      setShowDenyConfirm(false)
      setDenialReason('')
      fetchRequest()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al denegar')
    } finally {
      setIsProcessing(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-200 border-t-primary-600"></div>
      </div>
    )
  }

  if (!request) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">Solicitud no encontrada</p>
      </div>
    )
  }

  // Determinar si el usuario es el propietario de la solicitud
  const isOwner = user?.employeeId === request.employeeDbId

  // Permitir editar: admin puede editar PENDING y APPROVED, empleado solo sus propias PENDING
  const canEdit = isAdmin
    ? (request.status === 'PENDING' || request.status === 'APPROVED')
    : (request.status === 'PENDING' && isOwner)

  const tabs: TabItem[] = [
    { label: 'Información', path: '', icon: FileText, end: true },
    { label: 'Historial', path: 'history', icon: History },
  ]

  const contextValue: RequestContextType = {
    request,
    otherRequests,
    isLoading,
    isAdmin,
    isOwner,
    isEditing,
    isSaving,
    formData,
    setIsEditing,
    setFormData,
    handleSave,
    fetchRequest,
    canEdit,
  }

  return (
    <RequestContext.Provider value={contextValue}>
      <DetailLayout
        title="Detalle de Solicitud"
        subtitle={REQUEST_TYPE_LABELS[request.requestType]}
        backPath="/requests"
        basePath={`/requests/${id}`}
        tabs={tabs}
        avatar={
          <div className={`p-3 rounded-xl ${getStatusColor(request.status)}`}>
            <StatusIcon status={request.status} />
          </div>
        }
        badges={
          <>
            <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${getStatusColor(request.status)}`}>
              <StatusIcon status={request.status} />
              <span className="font-semibold">{REQUEST_STATUS_LABELS[request.status]}</span>
            </div>
            {request.isExtension && (
              <span className="badge badge-info">Prórroga</span>
            )}
          </>
        }
        actions={
          <>
            {/* Botones de aprobar/denegar para solicitudes PENDING (solo admin) */}
            {request.status === 'PENDING' && isAdmin && !isEditing && (
              <>
                <button
                  onClick={() => setShowApproveConfirm(true)}
                  disabled={isProcessing}
                  className="btn bg-success-600 hover:bg-success-700 text-white flex items-center gap-2 disabled:opacity-50"
                >
                  <CheckCircle className="h-4 w-4" />
                  <span className="hidden sm:inline">Aprobar</span>
                </button>
                <button
                  onClick={() => setShowDenyConfirm(true)}
                  disabled={isProcessing}
                  className="btn btn-danger flex items-center gap-2 disabled:opacity-50"
                >
                  <XCircle className="h-4 w-4" />
                  <span className="hidden sm:inline">Denegar</span>
                </button>
              </>
            )}
            {canEdit && !isEditing && (
              <>
                <button
                  onClick={() => setIsEditing(true)}
                  className="btn btn-primary flex items-center gap-2"
                >
                  <Edit2 className="h-4 w-4" />
                  <span className="hidden sm:inline">Editar</span>
                </button>
                {request.status === 'PENDING' && (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="btn btn-danger flex items-center gap-2"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span className="hidden sm:inline">Eliminar</span>
                  </button>
                )}
              </>
            )}
            {/* Botón de cancelar para solicitudes aprobadas (solo admin) */}
            {request.status === 'APPROVED' && !isEditing && isAdmin && (
              <button
                onClick={() => setShowCancelConfirm(true)}
                className="btn bg-warning-600 hover:bg-warning-700 text-white flex items-center gap-2"
              >
                <Ban className="h-4 w-4" />
                <span className="hidden sm:inline">Cancelar solicitud</span>
              </button>
            )}
            {isEditing && (
              <>
                <button
                  onClick={() => {
                    setIsEditing(false)
                    setFormData({
                      startDate: request.startDate.split('T')[0],
                      endDate: request.endDate.split('T')[0],
                      secondDate: request.secondDate ? request.secondDate.split('T')[0] : '',
                      notes: request.notes || '',
                      attachmentUrl: request.attachmentUrl || '',
                      attachmentName: request.attachmentName || '',
                    })
                  }}
                  className="btn btn-secondary flex items-center gap-2"
                  disabled={isSaving}
                >
                  <X className="h-4 w-4" />
                  Cancelar
                </button>
                <button
                  onClick={handleSave}
                  className="btn btn-primary flex items-center gap-2"
                  disabled={isSaving}
                >
                  <Save className="h-4 w-4" />
                  {isSaving ? 'Guardando...' : 'Guardar'}
                </button>
              </>
            )}
          </>
        }
      >
        <Outlet />
      </DetailLayout>

      {/* Delete Confirm */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Eliminar Solicitud"
        message="¿Estás seguro de que deseas eliminar esta solicitud? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        variant="danger"
      />

      {/* Cancel Confirmation Dialog */}
      {showCancelConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={() => {
              setShowCancelConfirm(false)
              setCancellationReason('')
            }} />
            <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-md w-full p-6 z-50">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                <Ban className="h-5 w-5 text-warning-600" />
                Cancelar solicitud
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                ¿Estás seguro de que deseas cancelar esta solicitud aprobada?
                <br />
                <span className="text-sm">Se notificará por email y en la aplicación.</span>
              </p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Motivo de cancelación (opcional)
                </label>
                <textarea
                  className="input w-full min-h-[80px]"
                  placeholder="Ej: No puedo disfrutar del permiso por motivos personales..."
                  value={cancellationReason}
                  onChange={(e) => setCancellationReason(e.target.value)}
                />
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowCancelConfirm(false)
                    setCancellationReason('')
                  }}
                  className="btn btn-secondary"
                >
                  Volver
                </button>
                <button
                  onClick={handleCancel}
                  className="btn bg-warning-600 hover:bg-warning-700 text-white"
                >
                  Cancelar solicitud
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Approve Confirmation Dialog */}
      {showApproveConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={() => {
              setShowApproveConfirm(false)
              setApprovalNotes('')
            }} />
            <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-md w-full p-6 z-50">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-success-600" />
                Aprobar solicitud
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                ¿Estás seguro de que deseas aprobar la solicitud de <strong>{request?.employeeName}</strong>?
                <br />
                <span className="text-sm">El empleado será notificado por email y notificación en la aplicación.</span>
              </p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Notas para el empleado (opcional)
                </label>
                <textarea
                  className="input w-full min-h-[80px]"
                  placeholder="Ej: Recuerda presentarte en RRHH el día anterior..."
                  value={approvalNotes}
                  onChange={(e) => setApprovalNotes(e.target.value)}
                />
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowApproveConfirm(false)
                    setApprovalNotes('')
                  }}
                  className="btn btn-secondary"
                  disabled={isProcessing}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleApprove}
                  className="btn bg-success-600 hover:bg-success-700 text-white"
                  disabled={isProcessing}
                >
                  {isProcessing ? 'Procesando...' : 'Aprobar solicitud'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Deny Confirmation Dialog */}
      {showDenyConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={() => {
              setShowDenyConfirm(false)
              setDenialReason('')
            }} />
            <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-md w-full p-6 z-50">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                <XCircle className="h-5 w-5 text-error-600" />
                Denegar solicitud
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                ¿Estás seguro de que deseas denegar la solicitud de <strong>{request?.employeeName}</strong>?
                <br />
                <span className="text-sm">El empleado será notificado por email y notificación en la aplicación.</span>
              </p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Motivo de denegación <span className="text-error-500">*</span>
                </label>
                <textarea
                  className="input w-full min-h-[80px]"
                  placeholder="Ej: No cumple con los requisitos de antigüedad..."
                  value={denialReason}
                  onChange={(e) => setDenialReason(e.target.value)}
                />
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowDenyConfirm(false)
                    setDenialReason('')
                  }}
                  className="btn btn-secondary"
                  disabled={isProcessing}
                >
                  Volver
                </button>
                <button
                  onClick={handleDeny}
                  className="btn btn-danger"
                  disabled={isProcessing || !denialReason.trim()}
                >
                  {isProcessing ? 'Procesando...' : 'Denegar solicitud'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </RequestContext.Provider>
  )
}
